import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "./ui/command";
import api, { getStudents, getStaff } from "../lib/api";

const SEARCH_ITEMS = [
  // Pages
  { label: "Dashboard", keywords: "home overview stats head count", path: "/", group: "Pages" },
  { label: "Attendance", keywords: "check in check out present absent tardy", path: "/attendance", group: "Pages" },
  { label: "Assessments", keywords: "individual assessment scores rating block class", path: "/assessment", group: "Pages" },
  { label: "Advisory", keywords: "advisory group weekly summary", path: "/advisory", group: "Pages" },
  { label: "Student Files", keywords: "students demographics info roster", path: "/students", group: "Pages" },
  { label: "Schedules & Calendar", keywords: "calendar schedule events", path: "/calendar", group: "Pages" },
  { label: "Reports and Lists", keywords: "reports billing attendance group lists", path: "/reports", group: "Pages" },
  { label: "Contacts", keywords: "contacts phone email directory", path: "/contacts", group: "Pages" },
  { label: "Settings", keywords: "settings preferences personalize groups classes", path: "/settings", group: "Pages" },
  { label: "Permissions", keywords: "permissions roles admin access", path: "/permissions", group: "Pages" },
  { label: "Archives", keywords: "archives historical data previous year archived students attendance", path: "/archives", group: "Pages" },
  { label: "Staff Files", keywords: "staff employee teacher", path: "/staff-files", group: "Pages" },

  // Calendar & Scheduling
  { label: "Master Schedule", keywords: "master schedule blocks classes weekly grid", path: "/calendar", tab: "master-schedule", group: "Calendar & Scheduling" },
  { label: "Meetings and Events", keywords: "meetings events calendar month", path: "/calendar", tab: "calendar", group: "Calendar & Scheduling" },
  { label: "School Calendar (Year)", keywords: "school calendar holidays year academic breaks", path: "/calendar", tab: "year-calendar", group: "Calendar & Scheduling" },
  { label: "Report a Snow Day / School Closure", keywords: "snow day closure unplanned day off cancel school weather emergency", path: "/calendar", tab: "year-calendar", group: "Calendar & Scheduling" },
  { label: "School Day Count", keywords: "school day count days in session how many days", path: "/", group: "Calendar & Scheduling" },

  // Student Features
  { label: "Add a Student", keywords: "add new student enroll create", path: "/students", group: "Student Actions" },
  { label: "Import Students from Spreadsheet", keywords: "import upload bulk spreadsheet csv excel students", path: "/students", group: "Student Actions" },
  { label: "Export Student Data", keywords: "export download student data spreadsheet", path: "/students", group: "Student Actions" },
  { label: "Print Student One Sheets", keywords: "one sheet print demographics parent info send home", path: "/students", group: "Student Actions" },
  { label: "Print Address Labels", keywords: "address labels avery mailing print labels", path: "/students", group: "Student Actions" },
  { label: "Student Self-Assessment", keywords: "self assessment student self rate score passcode", path: "/self-assessment", group: "Student Actions" },

  // Reports Features
  { label: "Submit a Report (Incident / Accident)", keywords: "incident accident report submit create injury behavior", path: "/reports", tab: "create", group: "Reports" },
  { label: "OTC / Medical Log", keywords: "otc medical medication log over the counter nurse", path: "/reports", tab: "medical", group: "Reports" },
  { label: "Student Attendance Report", keywords: "attendance report absent present tardy by student", path: "/reports", tab: "student-attendance", group: "Reports" },
  { label: "Attendance Summary Chart", keywords: "attendance summary chart director daily totals", path: "/reports", tab: "director-daily", group: "Reports" },
  { label: "Student Hours", keywords: "student hours director instructional time totals", path: "/reports", tab: "director-students", group: "Reports" },
  { label: "Snapshot Report", keywords: "snapshot report point in time overview", path: "/reports", tab: "snapshot", group: "Reports" },
  { label: "Assessments Report", keywords: "assessments report scores ratings history", path: "/reports", tab: "assessments", group: "Reports" },
  { label: "Group Lists", keywords: "group lists morning afternoon advisory grade print", path: "/reports", tab: "group-lists", group: "Reports" },
  { label: "Sign-Up Rosters (Print)", keywords: "sign up roster print daily block who picked what", path: "/reports", tab: "signup-rosters", group: "Reports" },
  { label: "Phone List", keywords: "phone list contact numbers print directory", path: "/reports", tab: "phone-list", group: "Reports" },
  { label: "Billing Reports", keywords: "billing invoice district funding", path: "/reports", tab: "billing", group: "Reports" },
  { label: "QA Data", keywords: "qa data quality assurance export review", path: "/reports", tab: "qa-data", group: "Reports" },
  { label: "Log a Safety Drill", keywords: "fire drill lockdown evacuation safety drill log", path: "/reports", tab: "safety-drills", group: "Reports" },

  // Risk & Safety
  { label: "Risk Assessment", keywords: "risk assessment weekly submit my risk score population overall student rating fill out complete", path: "/reports", tab: "risk-assessment", group: "Risk & Safety" },
  { label: "At-Risk Students", keywords: "at risk students flag concern attendance behavior report", path: "/reports", tab: "at-risk", group: "Risk & Safety" },
  { label: "Risk Assessment Setup (Score Bands & Weekly Prompt)", keywords: "risk assessment settings configure score bands low moderate high weekly prompt turn on off staff dashboard reminder push", path: "/settings", tab: "risk", group: "Risk & Safety" },

  // Communications & Tasks
  { label: "Communication Logs", keywords: "communications log call text email task reminder follow up parent district", path: "/communications", group: "Communications" },
  { label: "Quick Task", keywords: "quick task reminder to-do todo checklist add assignee due date", path: "/quick-tasks", group: "Quick Tasks" },
  { label: "My Open Tasks", keywords: "my tasks open assigned bell notifications follow up overdue", path: "/communications", group: "Communications" },

  // Sign-ups / Trips / Advisory
  { label: "Sign-Ups (Block 5 / Friday)", keywords: "sign ups block 5 options photography legos movement friday choose pick activity advisory", path: "/advisory", group: "Sign-Ups" },
  { label: "Trips & Events Sign-Up", keywords: "trips events camping mountain biking canoeing interest yes no maybe", path: "/advisory", group: "Sign-Ups" },
  { label: "Archived Sign-Ups", keywords: "archived sign ups past trips old events records keeper history", path: "/advisory", group: "Sign-Ups" },
  { label: "Manage Sign-Up Slots", keywords: "manage slots block 5 friday am pm configure rename", path: "/advisory", group: "Sign-Ups" },

  // Settings Features
  { label: "Classes / Groups Configuration", keywords: "morning groups afternoon groups classes core subjects configure", path: "/settings", tab: "class-groups", group: "Settings" },
  { label: "Advisory Groups Setup", keywords: "advisory groups advisor assign students configure", path: "/settings", tab: "advisory", group: "Settings" },
  { label: "Block Schedule Setup", keywords: "block schedule times full day half day custom configure", path: "/settings", tab: "block-schedule", group: "Settings" },
  { label: "Semesters / School Year", keywords: "semesters school year fall spring summer dates configure", path: "/settings", tab: "semesters", group: "Settings" },
  { label: "Self-Assessment Setup", keywords: "self assessment setup rubric subjects student portal configure", path: "/settings", tab: "self-assess", group: "Settings" },
  { label: "Proficiency Indicators", keywords: "proficiency indicators catalog standards configure", path: "/settings", tab: "proficiency", group: "Settings" },
  { label: "Communication Types", keywords: "communication types categories labels configure", path: "/settings", tab: "communication-types", group: "Settings" },
  { label: "Notifications & Email (Attendance Reminder Time)", keywords: "notifications email resend attendance reminder time bell notification absent students staff alert send time configure monday recap", path: "/settings", tab: "notifications", group: "Settings" },
  { label: "Data Management / Export", keywords: "data management export backup date range aoe filing", path: "/settings", tab: "data", group: "Settings" },
  { label: "School Branding", keywords: "branding logo colors school name customize", path: "/settings", tab: "branding", group: "Settings" },
  { label: "Set-Up Wizard", keywords: "setup wizard onboarding first time configure school", path: "/settings", tab: "setup-wizard", group: "Settings" },
  { label: "Audit Log", keywords: "audit log history changes who did what tracking", path: "/settings", tab: "audit", group: "Settings" },
  { label: "Personalize App", keywords: "personalize theme color school name customize", path: "/settings", tab: "personalize", group: "Settings" },

  // Staff Files Features
  { label: "Staff Excel Import / Export", keywords: "staff import export excel license expiry teacher data", path: "/staff-files", group: "Staff" },
  { label: "Staff One-Sheets (Print)", keywords: "staff one sheet print teacher info credentials license", path: "/staff-files", group: "Staff" },

  // New Year & Archives
  { label: "Start New School Year (Wizard)", keywords: "new school year wizard graduation rollover archive transition", path: "/students", group: "End of Year" },
  { label: "End of Year Reports", keywords: "end of year reports billing summary attendance summary", path: "/reports", group: "End of Year" },
];

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [students, setStudents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  // Keyboard shortcut: Ctrl+K or Cmd+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Lazy-load student files + contacts the first time the dialog is opened
  useEffect(() => {
    if (!open || loaded) return;
    (async () => {
      try {
        const [stuRes, conRes, staffRes] = await Promise.all([
          getStudents(null, true).catch(() => ({ data: [] })),
          api.get("/contacts").catch(() => ({ data: [] })),
          getStaff().catch(() => ({ data: [] })),
        ]);
        setStudents(stuRes.data || []);
        setContacts(conRes.data || []);
        setStaff(staffRes.data || []);
      } finally {
        setLoaded(true);
      }
    })();
  }, [open, loaded]);

  const handleSelect = (item) => {
    setOpen(false);
    const url = item.tab ? `${item.path}?tab=${item.tab}` : item.path;
    navigate(url);
  };

  const goTo = (path) => {
    setOpen(false);
    navigate(path);
  };

  const studentName = (s) => `${s.preferred_name || s.first_name || ""} ${s.last_name || ""}`.trim();
  const q = query.trim();

  // Group items
  const groups = {};
  SEARCH_ITEMS.forEach((item) => {
    if (!groups[item.group]) groups[item.group] = [];
    groups[item.group].push(item);
  });

  return (
    <>
      {/* Search trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white/70 hover:text-white text-sm w-full"
        data-testid="global-search-btn"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
        </svg>
        <span className="flex-1 text-left">Search...</span>
        <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border border-white/20 bg-white/10 px-1.5 text-[10px] font-medium text-white/50">
          <span className="text-xs">&#8984;</span>K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
        <CommandInput value={query} onValueChange={setQuery} placeholder="Search students, contacts, pages, or actions..." data-testid="global-search-input" />
        <CommandList>
          <CommandEmpty>No results found. Try different keywords.</CommandEmpty>

          {/* Live student files — only when actively searching */}
          {q && students.length > 0 && (
            <CommandGroup heading="Student Files">
              {students.map((s) => (
                <CommandItem
                  key={`stu-${s.id}`}
                  value={`student ${studentName(s)} ${s.first_name || ""} ${s.last_name || ""} ${s.preferred_name || ""}`}
                  onSelect={() => goTo(`/students/${s.id}`)}
                  className="cursor-pointer"
                  data-testid={`search-student-${s.id}`}
                >
                  <span>{studentName(s)}</span>
                  {s.grade && <span className="ml-2 text-xs text-muted-foreground">Grade {s.grade}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Live contacts — only when actively searching */}
          {q && contacts.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Contacts">
                {contacts.map((c) => (
                  <CommandItem
                    key={`con-${c.id}`}
                    value={`contact ${c.name || ""} ${c.organization || ""} ${c.title || ""}`}
                    onSelect={() => goTo(`/contacts?tab=${c.category || "district"}&search=${encodeURIComponent(c.name || "")}`)}
                    className="cursor-pointer"
                    data-testid={`search-contact-${c.id}`}
                  >
                    <span>{c.name}</span>
                    {(c.title || c.organization) && <span className="ml-2 text-xs text-muted-foreground">{[c.title, c.organization].filter(Boolean).join(" · ")}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Live staff files — only when actively searching */}
          {q && staff.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Staff Files">
                {staff.map((m) => (
                  <CommandItem
                    key={`staff-${m.id}`}
                    value={`staff ${m.name || ""} ${(m.positions || []).join(" ")}`}
                    onSelect={() => goTo(`/staff-files?staff=${m.id}`)}
                    className="cursor-pointer"
                    data-testid={`search-staff-${m.id}`}
                  >
                    <span>{m.name}</span>
                    {m.positions && m.positions.length > 0 && <span className="ml-2 text-xs text-muted-foreground">{m.positions.join(", ")}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {(q && (students.length > 0 || contacts.length > 0 || staff.length > 0)) && <CommandSeparator />}

          {Object.entries(groups).map(([groupName, items], gi) => (
            <div key={groupName}>
              {gi > 0 && <CommandSeparator />}
              <CommandGroup heading={groupName}>
                {items.map((item) => (
                  <CommandItem
                    key={`${item.path}-${item.label}`}
                    value={`${item.label} ${item.keywords}`}
                    onSelect={() => handleSelect(item)}
                    className="cursor-pointer"
                    data-testid={`search-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span>{item.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
